import { Link } from 'wouter';
import { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Typewriter } from '@/components/ui/typewriter';
import { Blueprint } from '@/components/art/blueprint';
import { BlueprintMobile } from '@/components/art/blueprint-mobile';
import { Lift } from '@/components/motion/reveal';

// Homepage hero: rotating headline over the blueprint art, fading and lifting on scroll.
export function Hero() {
  const ref = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -190]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.93]);
  const style = reduce ? undefined : { y, opacity, scale };

  return (
    <section ref={ref} className="hero-section relative overflow-hidden bg-[#0a0a0b]">
      <Blueprint className="hidden lg:block" />

      <motion.div
        style={style}
        className="pt-16 sm:pt-24 md:pt-28 pb-8 sm:pb-10 md:pb-12 max-w-7xl mx-auto w-full px-4 relative z-10 origin-top-left"
      >
        <div className="max-w-5xl">
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-white/50 font-medium mb-6 uppercase text-xs tracking-[0.3em]"
          >
            Leader · Builder · Operator
          </motion.p>
          <h1
            className="text-[2.6rem] sm:text-6xl lg:text-[5rem] font-extrabold leading-[1.02] sm:leading-[0.98] text-white mb-6 sm:mb-8 min-h-[2.4em] lg:max-w-[800px]"
            style={{ letterSpacing: '-0.03em' }}
          >
            <Typewriter
              staticText="7Sigma "
              phrases={[
                'picks up where your team leaves off.',
                'builds the foundation.',
                'scales what the team outgrew.',
                'ships the hard part.',
              ]}
              speed={55}
              deleteSpeed={30}
            />
          </h1>
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="text-base md:text-xl text-white/70 max-w-xl mb-8 leading-relaxed"
          >
            <a href="/fractional-cto" className="text-white hover:text-[#2563eb] transition-colors">
              Fractional CTO
            </a>
            , senior architecture, and technical leadership. Two or three clients at a time.
          </motion.p>
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Lift>
              <Link href="/contact">
                <Button
                  size="lg"
                  className="button-primary py-5 px-7 text-base sm:py-6 sm:px-8 sm:text-lg"
                >
                  Book a meeting
                </Button>
              </Link>
            </Lift>
            <Lift>
              <a href="#background">
                <Button
                  size="lg"
                  className="button-secondary py-5 px-7 text-base sm:py-6 sm:px-8 sm:text-lg"
                  variant="ghost"
                >
                  See the background
                </Button>
              </a>
            </Lift>
          </motion.div>
          <BlueprintMobile className="lg:hidden mt-10" />
        </div>
      </motion.div>
    </section>
  );
}
